import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createReturn,
  listReturnsByEvent,
  listReturnsByTransaction,
  listAllReturns,
  getReturn,
  type CreateReturnInput,
} from '@/lib/api/returns';
import { transactionKeys } from '@/hooks/useTransactions';

const KEYS = {
  all: ['returns'] as const,
  byEvent: (eventId: string | null) => [...KEYS.all, 'byEvent', eventId ?? ''] as const,
  byTransaction: (txId: string | null) => [...KEYS.all, 'byTransaction', txId ?? ''] as const,
  recent: (limit: number) => [...KEYS.all, 'recent', limit] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export const useReturnsByEvent = (eventId: string | null) =>
  useQuery({
    queryKey: KEYS.byEvent(eventId),
    queryFn: () => listReturnsByEvent(eventId),
    enabled: !!eventId,
  });

/**
 * Returns already recorded against one invoice. The return form needs the sum
 * of their refunds to preview the next refund the way create_return() computes
 * it — from the invoice's running returned total, not this return alone.
 */
export const useReturnsByTransaction = (txId: string | null) =>
  useQuery({
    queryKey: KEYS.byTransaction(txId),
    queryFn: () => (txId ? listReturnsByTransaction(txId) : Promise.resolve([])),
    enabled: !!txId,
  });

export const useRecentReturns = (limit = 50) =>
  useQuery({
    queryKey: KEYS.recent(limit),
    queryFn: () => listAllReturns(limit),
  });

export const useReturn = (id: string | null) =>
  useQuery({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: () => (id ? getReturn(id) : Promise.resolve(null)),
    enabled: !!id,
  });

export const useCreateReturn = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReturnInput) => createReturn(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: transactionKeys.detail(vars.transactionId) });
    },
  });
};
